import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../utils/axios";

const initialState = {
  isLoading: false,
  error: null,
  badges: {
    data: [],
    meta: {
      total: 0,
    },
  },
  userBadges: {
    data: [],
    meta: {
      total: 0,
    },
  },
};

// Fetch all badges
export const fetchBadges = createAsyncThunk(
  "fetchBadges/badges",
  async ({ limit = 10, page = 0, filters = {} }, thunkApi) => {
    try {
      const response = await axiosInstance.get("settings/badges", {
        params: {
          page: page + 1,
          limit,
          ...filters,
        },
      });

      return {
        data: response.data.data.items,
        meta: response.data.data.meta,
      };
    } catch (error) {
      return thunkApi.rejectWithValue({ error });
    }
  }
);

// Fetch badges for a specific user
export const fetchUserBadges = createAsyncThunk(
  "fetchUserBadges/badges",
  async ({ userId, limit = 10, page = 0 }, thunkApi) => {
    try {
      const response = await axiosInstance.get(`users/get-badges`, {
        params: {
          user_id: userId,
          page: page + 1,
          limit,
        },
      });

      return {
        data: response.data.data.items,
        meta: response.data.data.meta,
      };
    } catch (error) {
      return thunkApi.rejectWithValue({ error });
    }
  }
);

// Create badge
export const createBadge = createAsyncThunk(
  "createBadge/badges",
  async ({ data, enqueueSnackbar, handleClose }, thunkApi) => {
    try {
      const response = await axiosInstance.post(`settings/badges`, data);

      return {
        data: response.data.data,
        handleClose,
        enqueueSnackbar,
      };
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

// Update badge
export const updateBadge = createAsyncThunk(
  "updateBadge/badges",
  async ({ data, enqueueSnackbar, handleClose, id }, thunkApi) => {
    try {
      const response = await axiosInstance.post(`settings/badges/${id}`, data);

      return {
        data: response.data.data,
        handleClose,
        enqueueSnackbar,
      };
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

// Delete badge
export const deleteBadge = createAsyncThunk(
  "deleteBadge/badges",
  async ({ enqueueSnackbar, handleClose, id }, thunkApi) => {
    try {
      await axiosInstance.delete(`settings/badges/${id}`);
      return {
        data: id,
        handleClose,
        enqueueSnackbar,
      };
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

const badgeSlice = createSlice({
  name: "badge",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Fetch all badges
    builder
      .addCase(fetchBadges.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBadges.fulfilled, (state, action) => {
        state.isLoading = false;
        state.badges = action.payload;
      })
      .addCase(fetchBadges.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.error;
      });

    // Fetch user badges
    builder
      .addCase(fetchUserBadges.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserBadges.fulfilled, (state, action) => {
        state.isLoading = false;
        state.userBadges = action.payload;
      })
      .addCase(fetchUserBadges.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.error;
      });

    // Create badge
    builder
      .addCase(createBadge.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createBadge.fulfilled, (state, action) => {
        state.isLoading = false;
        state.badges.data = [action.payload.data, ...state.badges.data];
        state.badges.meta.total += 1;
        action.payload.enqueueSnackbar("Badge created successfully", {
          variant: "success",
        });
        action.payload.handleClose && action.payload.handleClose();
      })
      .addCase(createBadge.rejected, (state, action) => {
        state.isLoading = false;
        action.payload.enqueueSnackbar(
          action.payload.error?.message || "Failed to create badge",
          { variant: "error" }
        );
      });

    // Update badge
    builder
      .addCase(updateBadge.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateBadge.fulfilled, (state, action) => {
        state.isLoading = false;
        state.badges.data = state.badges.data.map((badge) =>
          badge.id === action.payload.data.id ? action.payload.data : badge
        );
        action.payload.enqueueSnackbar("Badge updated successfully", {
          variant: "success",
        });
        action.payload.handleClose && action.payload.handleClose();
      })
      .addCase(updateBadge.rejected, (state, action) => {
        state.isLoading = false;
        action.payload.enqueueSnackbar(
          action.payload.error?.message || "Failed to update badge",
          { variant: "error" }
        );
      });

    // Delete badge
    builder
      .addCase(deleteBadge.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteBadge.fulfilled, (state, action) => {
        state.isLoading = false;
        state.badges.data = state.badges.data.filter(
          (badge) => badge.id !== action.payload.data
        );
        state.badges.meta.total -= 1;
        action.payload.enqueueSnackbar("Badge deleted successfully", {
          variant: "success",
        });
        action.payload.handleClose && action.payload.handleClose();
      })
      .addCase(deleteBadge.rejected, (state, action) => {
        state.isLoading = false;
        action.payload.enqueueSnackbar(
          action.payload.error?.message || "Failed to delete badge",
          { variant: "error" }
        );
      });
  },
});

export default badgeSlice.reducer;
